import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import BankScopeField from '../../components/BankScopeField';
import ScopeField from '../../components/ScopeField';
import {
  createKonbinezonRule, deleteKonbinezonRule, listKonbinezonRules, updateKonbinezonRule,
} from '../../api/konbinezonRule';
import './KonbinezonRatioPage.css';

// RÈG 1 — Ratio konbinezon minimòm selon kantite boul.
// Portée : company (Tout) / group / bank — le plus spécifique gagne côté vente
// (products/services.py:resolve_konbinezon_ratio_rule). Au plus une règle par cible.
//   konbinezon_total >= bor_total * P / 100   dès que (nb BOUL BOR distincts hors paires) >= N
// Exception : les 10 paires 00,11,...,99 ne comptent pas dans le seuil N.

const BALL_OPTIONS = Array.from({ length: 19 }, (_, i) => (i + 1) * 5);   // 5 .. 95
const PCT_OPTIONS = Array.from({ length: 10 }, (_, i) => i + 6);          // 6 .. 15
const DOUBLES = ['00', '11', '22', '33', '44', '55', '66', '77', '88', '99'];

const DEFAULTS = { enabled: false, min_balls: 20, min_pct: 6 };

const scopeParams = (filter, scopeId) => {
  if (filter === 'Groupe') return { group: scopeId };
  if (filter === 'Banque') return { bank: scopeId };
  return {};
};

const KonbinezonRatioPage = () => {
  const [filter, setFilter] = useState('Tout');
  const [scopeId, setScopeId] = useState('');
  const [scopeName, setScopeName] = useState('');

  const [ruleId, setRuleId] = useState(null);
  const [enabled, setEnabled] = useState(DEFAULTS.enabled);
  const [minBalls, setMinBalls] = useState(DEFAULTS.min_balls);
  const [minPct, setMinPct] = useState(DEFAULTS.min_pct);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const scopeReady = filter === 'Tout' || !!scopeId;

  const load = useCallback(() => {
    if (!scopeReady) return;
    setLoading(true);
    setError('');
    setSaved(false);
    listKonbinezonRules(scopeParams(filter, scopeId))
      .then((rows) => {
        const rule = Array.isArray(rows) ? rows[0] : null;
        if (rule) {
          setRuleId(rule.id);
          setEnabled(rule.enabled);
          setMinBalls(Number(rule.min_balls));
          setMinPct(Math.round(Number(rule.min_pct)));
        } else {
          setRuleId(null);
          setEnabled(DEFAULTS.enabled);
          setMinBalls(DEFAULTS.min_balls);
          setMinPct(DEFAULTS.min_pct);
        }
      })
      .catch(() => setError('Impossible de charger la règle.'))
      .finally(() => setLoading(false));
  }, [filter, scopeId, scopeReady]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSaved(false);
    const payload = { enabled, min_balls: minBalls, min_pct: minPct };
    try {
      if (ruleId) {
        await updateKonbinezonRule(ruleId, payload);
      } else {
        const created = await createKonbinezonRule({ ...payload, ...scopeParams(filter, scopeId) });
        setRuleId(created.id);
      }
      setSaved(true);
    } catch (err) {
      setError('Sauvegarde impossible. Vérifiez les valeurs.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!ruleId) return;
    setSaving(true);
    setError('');
    try {
      await deleteKonbinezonRule(ruleId);
      setRuleId(null);
      setEnabled(DEFAULTS.enabled);
      setMinBalls(DEFAULTS.min_balls);
      setMinPct(DEFAULTS.min_pct);
      setSaved(false);
    } catch (err) {
      setError('Suppression impossible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <p className="kr-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <Link to="/securite" className="bc-link">Securite</Link>
        {' >> '}
        <span className="bc-current">Ratio Konbinezon</span>
      </p>

      <div className="kr-card">
        <div className="kr-card-header">
          <span className="kr-card-title">Ratio Konbinezon Minimòm (RÈG 1)</span>
        </div>
        <div className="kr-card-body">
          <div className="kr-radio-row">
            {['Tout', 'Groupe', 'Banque'].map((opt) => (
              <label key={opt} className="kr-radio-label">
                <input
                  type="radio"
                  name="kr-filter"
                  value={opt}
                  checked={filter === opt}
                  onChange={() => { setFilter(opt); setScopeId(''); setScopeName(''); }}
                />
                {opt}
              </label>
            ))}
          </div>

          {filter !== 'Tout' && (
            <div className="kr-row">
              <label className="kr-label">{filter}</label>
              {filter === 'Banque' ? (
                <BankScopeField
                  id={scopeId}
                  name={scopeName}
                  onResolve={(id, name) => { setScopeId(id); setScopeName(name); }}
                  inputClass="kr-input kr-input-sm"
                  iconClass="kr-icon-btn"
                  nameClass="kr-scope-name"
                  errClass="kr-lookup-err"
                />
              ) : (
                <ScopeField
                  filter={filter}
                  id={scopeId}
                  name={scopeName}
                  onResolve={(id, nm) => { setScopeId(id); setScopeName(nm); }}
                  inputClass="kr-input kr-input-sm"
                  iconClass="kr-icon-btn"
                  nameClass="kr-scope-name"
                  errClass="kr-lookup-err"
                />
              )}
            </div>
          )}

        </div>
      </div>

      <div className="kr-card">
        <div className="kr-card-header">
          <span className="kr-card-title">
            Règle {filter === 'Tout' ? 'générale (company)' : `${filter.toLowerCase()}`}
          </span>
        </div>

        {!scopeReady ? (
          <div className="kr-card-body">
            <p className="kr-hint">Choisir un {filter.toLowerCase()} ci-dessus.</p>
          </div>
        ) : loading ? (
          <div className="kr-card-body"><p>Chargement...</p></div>
        ) : (
          <form className="kr-card-body" onSubmit={handleSave}>
            <label className="kr-toggle-row">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => { setEnabled(e.target.checked); setSaved(false); }}
              />
              <span>Aktive règ la</span>
              {!ruleId && <span className="kr-hint">(pas encore de règle pour cette portée)</span>}
            </label>

            <div className="kr-row">
              <label className="kr-label">Apati de (boul)</label>
              <select
                className="kr-input"
                value={minBalls}
                disabled={!enabled}
                onChange={(e) => { setMinBalls(Number(e.target.value)); setSaved(false); }}
              >
                {BALL_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <span className="kr-hint">boul BOR distenk (san pè yo)</span>
            </div>

            <div className="kr-row">
              <label className="kr-label">Pousantaj min.</label>
              <select
                className="kr-input"
                value={minPct}
                disabled={!enabled}
                onChange={(e) => { setMinPct(Number(e.target.value)); setSaved(false); }}
              >
                {PCT_OPTIONS.map((p) => <option key={p} value={p}>{p} %</option>)}
              </select>
              <span className="kr-hint">sou total mis BOR yo</span>
            </div>

            <div className="kr-btn-row">
              <button type="submit" className="kr-save-btn" disabled={saving}>
                {ruleId ? 'Anrejistre' : 'Kreye règ la'}
              </button>
              {ruleId && (
                <button type="button" className="kr-del-btn" onClick={handleDelete} disabled={saving}>
                  Efase
                </button>
              )}
            </div>

            {saved && <p className="kr-ok">Konfigirasyon anrejistre.</p>}
            {error && <p className="kr-error">{error}</p>}
          </form>
        )}
      </div>

      <div className="kr-card">
        <div className="kr-card-header">
          <span className="kr-card-title">Kijan sa mache</span>
        </div>
        <div className="kr-card-body">
          <p className="kr-explain">
            Lè yon fich gen <strong>{minBalls} boul</strong> BOR distenk oswa plis
            (san konte 10 pè yo : {DOUBLES.join(', ')}), total mis sou konbinezon yo
            (Lotto&nbsp;3, Mariaj, Lotto&nbsp;4, Lotto&nbsp;5) dwe <strong>≥ {minPct} %</strong>{' '}
            total mis BOR yo. Si li pi piti → fich la <strong>bloke</strong>.
          </p>
          <p className="kr-explain kr-explain-ex">
            Egzanp : total BOR = 100 G, pousantaj = {minPct} % → konbinezon yo dwe ≥ {minPct} G.
          </p>
          <p className="kr-explain kr-explain-note">
            Règ la pa aplike pou fich ki gen mwens pase {minBalls} boul, ni sou 10 pè
            yo pou konte kantite boul la. Portée pi presi (bank {'>'} group {'>'} company)
            genyen : yon règ bank ranplase règ group/company pou vandè sa a.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default KonbinezonRatioPage;
